import * as React from 'react';
import {
  MessageBar,
  MessageBarType,
  IMessageBarStyles,
  IStyleFunctionOrObject,
  ITheme,
  styled,
  IStyle,
  IIconStyles,
  concatStyleSetsWithProps,
  mergeStyles,
  Link
} from '@fluentui/react';
import { BannerHyperlink } from '@fluidx/office-fluid-types';

export interface MessageBannerStrings {
  /** Lead, bolded text of the banner */
  bannerTitle: string;

  /** A user friendly error message */
  bannerMessage: string;
}

export interface MessageBannerProps {
  /** Strings to render in banner */
  bannerStrings: MessageBannerStrings;

  /** Type of MessageBar */
  messageBarType: MessageBarType;

  /** Recovery actions you want to render alongside banner message */
  actions?: JSX.Element[];

  /** Allow text wrapping of message banner content */
  isMultiline?: boolean;

  /** Enable aria alerts on banner */
  isAssertive?: boolean;

  /** Custom styling for the message bar */
  styles?: IStyleFunctionOrObject<MessageBannerStyleProps, MessageBannerStyles>;

  /** Theme applied through customizer */
  theme?: ITheme;

  /**
   * The name of the icon to be used when displaying the command. These icon
   * names correspond to the icons defined in @fluidx/icons.
   *
   * TODO: Provide svg instead of just icon name string, to allow for custom icons.
   */
  icon?: string;

  /** Enable/disable dismiss icon on banner */
  onDismiss?: () => void;

  /** Aria label for dismiss button when rendered */
  dismissButtonAriaLabel?: string;

  /** Adds a hyperlink at the end of the message */
  hyperlink?: BannerHyperlink;
}

export interface MessageBannerStyles extends Omit<IMessageBarStyles, 'innerText' | 'action' | 'icon'> {
  /** Style set for lead, bolded text in the message bar */
  titleText?: IStyle;

  /** Style set for the message bar message text */
  messageText?: IStyle;

  /** Style set for the optional element containing the action elements */
  actionStyles?: IStyle;

  /** Style set for the icon */
  iconStyles?: IIconStyles;
}

export interface MessageBannerStyleProps extends Pick<MessageBannerProps, 'theme'> {}

export const MessageBanner = React.memo(
  styled((props: MessageBannerProps) => {
    const {
      bannerStrings,
      actions,
      icon,
      messageBarType,
      isMultiline,
      isAssertive,
      styles,
      onDismiss,
      dismissButtonAriaLabel,
      hyperlink
    } = props;

    // a11y: use assertive banners
    const assertiveBanner = isAssertive ? 'alert' : undefined;

    // a11y: use polite banners in non-assertive cases
    const politeBanner = assertiveBanner ? undefined : 'polite';

    const { root, content, text, titleText, messageText, actionStyles, iconStyles } = concatStyleSetsWithProps(
      props,
      styles
    );

    return (
      <MessageBar
        styles={{ root, content, text, actions: actionStyles }}
        messageBarType={messageBarType}
        isMultiline={isMultiline}
        tabIndex={0}
        ariaLabel={bannerStrings.bannerMessage}
        role={assertiveBanner}
        aria-live={politeBanner}
        messageBarIconProps={{
          iconName: icon,
          styles: iconStyles
        }}
        actions={actions ? <>{actions}</> : undefined}
        onDismiss={onDismiss}
        dismissButtonAriaLabel={dismissButtonAriaLabel}
      >
        {bannerStrings.bannerTitle && <MessageBarTitle styles={titleText}>{bannerStrings.bannerTitle}</MessageBarTitle>}
        <MessageBarMessage styles={messageText}>{bannerStrings.bannerMessage}</MessageBarMessage>
        {hyperlink !== undefined && (
          <Link href={hyperlink.url} target={hyperlink.target || '_self'} underline={true}>
            {hyperlink.title}
          </Link>
        )}
      </MessageBar>
    );
  }, getDefaultStyle)
);

function getDefaultStyle(_props: MessageBannerStyleProps): MessageBannerStyles {
  return {
    root: {
      minHeight: 36
    },
    content: {
      alignItems: 'center'
    },
    text: {
      fontSize: 14,
      flexGrow: 1
    },
    titleText: {
      fontWeight: 600,
      display: 'inline-block'
    },
    messageText: {
      lineHeight: 'normal',
      display: 'inline-block'
    },
    actionStyles: {
      marginRight: '8px'
    },
    iconStyles: {
      root: {
        fontWeight: 400
      }
    }
  };
}

const MessageBarTitle = React.memo((props: { children: React.ReactNode; styles?: IStyle }) => {
  const { children, styles } = props;

  return (
    <div className={mergeStyles(styles)}>
      {children}
      <span>&#160;&#160;</span>
    </div>
  );
});

const MessageBarMessage = React.memo((props: { children: React.ReactNode; styles?: IStyle }) => {
  const { children, styles } = props;

  return <div className={mergeStyles(styles)}>{children}</div>;
});

// cspell:ignore uneditable customizer
